/**
 * BehaviorCard — Yes/No checklist per FINN value behavior bullet,
 * mismatch warning, rating buttons, per-value detect from delivery,
 * collapsible context section (auto-opens when rated, manually openable before).
 */
import { useState } from 'react';
import { C, BEHAVIORS, getBehaviorGroup, getRatingColor, getRatingLabel } from '../../data/managerReviewData';
import type { BehaviorData, FinnValue } from '../../types/managerReview';
import ReviewTextarea from './ReviewTextarea';
import { detectBehaviorsForValue } from './behaviorDetect';

const renderBold = function(text: string): React.ReactNode {
  if (!text || !text.includes('**')) return text;
  const parts = text.split('**');
  return parts.map(function(p, i) { return i % 2 === 1 ? <strong key={i}>{p}</strong> : p; });
};

interface BehaviorCardProps {
  index: number;
  value: FinnValue;
  level: string;
  data: BehaviorData;
  onChange: (d: BehaviorData) => void;
  aiContext: Record<string, unknown>;
  nextBullets: string[] | null;
  nextLevelLabel: string | null;
  deliverables: Array<{ title?: string; wellDone?: string; improvement?: string }>;
  id?: string;
}

export default function BehaviorCard({
  index, value, level, data, onChange, aiContext,
  nextBullets, nextLevelLabel, deliverables, id,
}: BehaviorCardProps) {
  const group = getBehaviorGroup(level);
  const bullets = BEHAVIORS[group]?.[value.name] || [];
  const observed = data.observed || {};

  const answeredCount = bullets.filter(function(b) { return observed[b] !== undefined; }).length;
  const yesCnt = bullets.filter(function(b) { return observed[b] === true; }).length;
  const allAnswered = answeredCount === bullets.length;
  const allYes = allAnswered && yesCnt === bullets.length;
  const allNo = allAnswered && yesCnt === 0;
  const pctYes = answeredCount > 0 ? yesCnt / answeredCount : null;
  const mismatch = data.rating && (
    (allNo && data.rating >= 3) ||
    (pctYes !== null && pctYes < 1 && data.rating >= 4) ||
    (allYes && data.rating <= 2)
  );

  const toggle = function(bullet: string, val: boolean) {
    const next = observed[bullet] === val ? undefined : val;
    const updated = { ...observed };
    if (next === undefined) delete updated[bullet]; else updated[bullet] = next;
    onChange({ ...data, observed: updated });
  };

  const accentColor = data.rating ? getRatingColor(data.rating) : C.border;
  const [collapsed, setCollapsed] = useState(false);
  const [contextExpanded, setContextExpanded] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [detectError, setDetectError] = useState('');
  const [detectCount, setDetectCount] = useState<number | null>(null);

  // Auto-opens when rated; also manually openable before rating
  const showContext = contextExpanded || !!data.rating;
  const hasDeliveryNotes = deliverables.some(d => d.wellDone?.trim() || d.improvement?.trim());

  const handleDetect = async () => {
    setDetecting(true);
    setDetectError('');
    setDetectCount(null);
    try {
      const matches = await detectBehaviorsForValue(deliverables, value.name);
      if (matches.length === 0) {
        setDetectCount(0);
      } else {
        let updatedData = { ...data };
        let count = 0;
        matches.forEach(m => {
          if (m.sentiment === 'positive') {
            const existing = updatedData.strengthsText || '';
            if (!existing.includes(m.excerpt.substring(0, 30))) {
              updatedData = { ...updatedData, strengthsText: (existing ? existing + '\n\n' : '') + '[From Delivery] ' + m.excerpt };
              count++;
            }
          } else {
            const existing = updatedData.improvementsText || '';
            if (!existing.includes(m.excerpt.substring(0, 30))) {
              updatedData = { ...updatedData, improvementsText: (existing ? existing + '\n\n' : '') + '[From Delivery] ' + m.excerpt };
              count++;
            }
          }
        });
        onChange(updatedData);
        setDetectCount(count);
      }
    } catch {
      setDetectError('Detection failed — please try again.');
    } finally {
      setDetecting(false);
    }
  };

  // Behavior bullets as context for the coach
  const behaviorBullets = bullets.map(function(b) { return b.replace(/\*\*/g, ''); });
  const observedYesBullets = behaviorBullets.filter(function(_b, i) { return observed[bullets[i]] === true; });
  const observedNoBullets = behaviorBullets.filter(function(_b, i) { return observed[bullets[i]] === false; });

  return (
    <div id={id} style={{
      marginBottom: 12, borderRadius: 8, border: '1px solid #e8e8e8',
      borderLeft: '3px solid ' + accentColor, overflow: 'hidden', background: '#fff',
    }}>
      <div
        onClick={function() { setCollapsed(function(c) { return !c; }); }}
        style={{
          display: 'flex', alignItems: 'center', padding: '10px 16px',
          background: '#f5f5f5', borderBottom: collapsed ? 'none' : '1px solid #ebebeb',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: 13, color: '#111', letterSpacing: '-0.01em' }}>
          <span style={{ display: 'inline-block', transition: 'transform 0.15s', transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)', fontSize: 9 }}>▶</span>
          <div style={{
            width: 22, height: 22, borderRadius: 6, background: '#1a1a1a', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 800, flexShrink: 0,
          }}>{index + 1}</div>
          {value.name}
        </div>
        {!collapsed && <div style={{ width: 40, textAlign: 'center', fontSize: 10, fontWeight: 600, color: C.green, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Yes</div>}
        {!collapsed && <div style={{ width: 40, textAlign: 'center', fontSize: 10, fontWeight: 600, color: C.red, textTransform: 'uppercase', letterSpacing: '0.05em' }}>No</div>}
      </div>

      {!collapsed && (
        <>
          {bullets.map(function(b, i) {
            const obs = observed[b];
            return (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', padding: '9px 16px',
                borderBottom: i < bullets.length - 1 ? '1px solid #f0f0f0' : 'none',
                background: '#fff', transition: 'background 0.1s',
              }}>
                <div style={{ flex: 1, fontSize: 12, lineHeight: 1.6, color: '#333', paddingRight: 10 }}>
                  {renderBold(b)}
                </div>
                {[true, false].map(function(val) {
                  const active = obs === val;
                  const col = val ? C.green : C.red;
                  return (
                    <div key={String(val)} style={{ width: 40, display: 'flex', justifyContent: 'center' }}>
                      <button
                        onClick={function(e) { e.stopPropagation(); toggle(b, val); }}
                        title={val ? 'Observed' : 'Not observed'}
                        style={{
                          width: 24, height: 24, borderRadius: '50%',
                          border: '2px solid ' + (active ? col : '#d1d5db'),
                          background: active ? col : '#fff',
                          cursor: 'pointer', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', transition: 'all 0.1s', flexShrink: 0,
                        }}
                      >
                        {active && <span style={{ color: '#fff', fontSize: 11, lineHeight: 1, fontWeight: 700 }}>✓</span>}
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          })}

          {nextBullets && nextBullets.length > 0 && (
            <div style={{ borderTop: '1px dashed #d8e6fa', background: '#f5f9ff', padding: '10px 16px' }}>
              <span style={{
                fontSize: 10, fontWeight: 700, color: C.blue,
                background: C.blue + '18', borderRadius: 4, padding: '2px 7px',
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>{nextLevelLabel}</span>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 5 }}>
                {nextBullets.map(function(b, i) {
                  return (
                    <div key={i} style={{ fontSize: 12, color: C.blue + 'aa', lineHeight: 1.6 }}>
                      • {renderBold(b)}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div style={{ padding: '12px 16px', background: '#f5f5f5', borderTop: '1px solid #ebebeb' }}>
            {/* Step 2: Rating — unlocked once checklist is complete */}
            <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 8 }}>
              Rating
            </div>
            {answeredCount < bullets.length ? (
              <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic', marginBottom: 10 }}>
                Complete the checklist above to rate this value.
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 5 }}>
                  {[1, 2, 3, 4, 5].map(function(r) {
                    const isSelected = data.rating === r;
                    const col = getRatingColor(r);
                    return (
                      <button
                        key={r}
                        onClick={function(e) { e.stopPropagation(); onChange({ ...data, rating: data.rating === r ? null : r }); }}
                        style={{
                          width: 34, height: 34, borderRadius: 6,
                          border: '1.5px solid ' + (isSelected ? col : '#d1d5db'),
                          background: isSelected ? col : '#fff',
                          color: isSelected ? '#fff' : '#111',
                          fontSize: 13, fontWeight: 700, cursor: 'pointer',
                          fontFamily: 'Inter', transition: 'all 0.1s',
                        }}
                      >{r}</button>
                    );
                  })}
                </div>
                <span style={{ fontSize: 12, color: data.rating ? getRatingColor(data.rating) : '#999', fontWeight: data.rating ? 600 : 400 }}>
                  {data.rating ? getRatingLabel(data.rating) : 'Select a rating'}
                </span>
              </div>
            )}

            {mismatch && (
              <div style={{ marginBottom: 8, fontSize: 11, color: '#1a1a1a', fontWeight: 600 }}>
                ⚠ Rating doesn't quite match your checklist observations.
              </div>
            )}

            {/* Step 3: Context — collapsed by default, auto-opens when rated */}
            <div
              onClick={() => setContextExpanded(e => !e)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                cursor: 'pointer', paddingTop: 8, marginTop: 2,
                borderTop: '1px solid #e2e2e2', userSelect: 'none',
              }}
            >
              <span style={{
                display: 'inline-block', transition: 'transform 0.15s',
                transform: showContext ? 'rotate(90deg)' : 'rotate(0deg)', fontSize: 9,
              }}>▶</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#555' }}>
                Add context
              </span>
              {!data.rating && !contextExpanded && (
                <span style={{ fontSize: 11, color: '#bbb', fontStyle: 'italic' }}>· rate this value first</span>
              )}
            </div>

            {showContext && (
              <div style={{ marginTop: 12 }}>
                {/* Per-value detect button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                  <button
                    onClick={handleDetect}
                    disabled={detecting || !hasDeliveryNotes}
                    title={!hasDeliveryNotes ? 'Add notes in the Delivery tab first' : `Scan delivery notes for evidence of ${value.name}`}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '5px 11px', borderRadius: 7, fontSize: 11, fontWeight: 600,
                      cursor: detecting || !hasDeliveryNotes ? 'not-allowed' : 'pointer',
                      fontFamily: 'Inter', transition: 'all 0.15s',
                      border: `1.5px solid ${detecting ? '#d1d5db' : hasDeliveryNotes ? C.blue : '#ddd'}`,
                      background: detecting ? '#f5f5f5' : hasDeliveryNotes ? C.lightBlue : '#fafafa',
                      color: detecting ? '#aaa' : hasDeliveryNotes ? C.blue : '#bbb',
                      opacity: !hasDeliveryNotes ? 0.55 : 1,
                    }}
                  >
                    {detecting ? '⏳ Detecting…' : '🔍 Detect from delivery'}
                  </button>
                  {!detecting && detectCount !== null && detectCount > 0 && (
                    <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>
                      ✓ {detectCount} insight{detectCount !== 1 ? 's' : ''} added
                    </span>
                  )}
                  {!detecting && detectCount === 0 && !detectError && (
                    <span style={{ fontSize: 11, color: C.muted, fontWeight: 500 }}>
                      No new insights found
                    </span>
                  )}
                  {detectError && (
                    <span style={{ fontSize: 11, color: '#e8294a', fontWeight: 600 }}>{detectError}</span>
                  )}
                </div>

                {index === 0 && (
                  <div style={{ fontSize: 10, color: '#bbb', lineHeight: 1.5, marginBottom: 14, fontStyle: 'italic' }}>
                    💡 Delivery notes are a starting point — also note day-to-day moments like team meetings, ad-hoc support, or FINN Station shifts.
                  </div>
                )}

                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>
                    Where did I see this behavior clearly demonstrated?
                  </div>
                  <ReviewTextarea
                    value={data.strengthsText || ''}
                    onChange={function(v) { onChange({ ...data, strengthsText: v }); }}
                    placeholder="What did they do particularly well for this value? Be specific. Type or hit Record to speak your thoughts."
                    voice
                    compact
                    aiContext={{ ...aiContext, field: 'strengths', availableBehaviors: behaviorBullets, observedBehaviors: observedYesBullets }}
                    onRedirected={function(text) { onChange({ ...data, improvementsText: (data.improvementsText ? data.improvementsText + '\n\n' : '') + text }); }}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>
                    Were there any gaps or inconsistencies in this behavior?
                  </div>
                  <ReviewTextarea
                    value={data.improvementsText || ''}
                    onChange={function(v) { onChange({ ...data, improvementsText: v }); }}
                    placeholder="Where could they have demonstrated this value more strongly? Type or hit Record to speak your thoughts."
                    voice
                    compact
                    aiContext={{ ...aiContext, field: 'improvements', availableBehaviors: behaviorBullets, notObservedBehaviors: observedNoBullets }}
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
